import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Project, Criterion, ScheduleItem } from '../types';
import { DEFAULT_PROJECTS, DEFAULT_PRIZE_TIERS } from '../data/defaultData';
import { 
  Lock, 
  Unlock, 
  Download, 
  RotateCcw, 
  Play, 
  Sparkles, 
  Plus, 
  Trash2, 
  Edit, 
  Users, 
  Settings as SettingsIcon, 
  Layers, 
  AlertTriangle, 
  Trophy, 
  Shield,
  Calendar,
  Clock,
  BookOpen,
  ExternalLink,
  RefreshCw,
  CheckCircle2
} from 'lucide-react';

export const AdminDashboard: React.FC = () => {
  const {
    isAdminAuthenticated,
    authenticateAdmin,
    logoutAdmin,
    settings,
    updateSettings,
    ballots,
    resetAllBallots,
    seedTestBallots,
    exportCSV,
    projects,
    addProject,
    updateProject,
    deleteProject,
    restoreDefaultProjects,
    restoreProject,
    criteria,
    updateCriterion,
    addCriterion,
    deleteCriterion,
    restoreDefaultCriteria,
    updatePrizeTiers,
    leaderboard,
    schedule,
    addScheduleItem,
    updateScheduleItem,
    deleteScheduleItem,
    restoreDefaultSchedule,
    refreshAllData,
    isRefreshing,
  } = useApp();

  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [activeAdminTab, setActiveAdminTab] = useState<'standings' | 'schedule' | 'projects' | 'criteria' | 'ballots' | 'settings'>('standings');
  const [confirmResetModal, setConfirmResetModal] = useState(false);
  const [confirmRestoreDefaultsModal, setConfirmRestoreDefaultsModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  // Schedule state
  const [editingScheduleItem, setEditingScheduleItem] = useState<ScheduleItem | null>(null);
  const [isAddingScheduleItem, setIsAddingScheduleItem] = useState(false);
  const [scheduleItemToDelete, setScheduleItemToDelete] = useState<ScheduleItem | null>(null);
  const [confirmRestoreScheduleModal, setConfirmRestoreScheduleModal] = useState(false);
  const [adminScheduleDayFilter, setAdminScheduleDayFilter] = useState<string>('all');
  const [newScheduleForm, setNewScheduleForm] = useState<Omit<ScheduleItem, 'id'>>({
    date: 'Tuesday, September 29, 2026',
    time: '',
    title: '',
    description: '',
    speakerOrLeader: '',
    category: 'general',
    isCurrent: false,
  });

  const adminUniqueDays = useMemo(() => {
    const days: string[] = [];
    schedule.forEach((item) => {
      const d = item.date?.trim() || 'General Schedule';
      if (!days.includes(d)) {
        days.push(d);
      }
    });
    return days;
  }, [schedule]);

  const filteredAdminSchedule = useMemo(() => {
    if (adminScheduleDayFilter === 'all') return schedule;
    return schedule.filter((s) => (s.date?.trim() || 'General Schedule') === adminScheduleDayFilter);
  }, [schedule, adminScheduleDayFilter]);

  const missingDefaultProjects = DEFAULT_PROJECTS.filter(
    (dp) => !projects.some((p) => p.id === dp.id)
  );

  // Edit project state
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({
    title: '',
    presenter: '',
    website: '',
    purpose: '',
  });

  // Criteria state
  const [editingCriterion, setEditingCriterion] = useState<Criterion | null>(null);
  const [isAddingCriterion, setIsAddingCriterion] = useState(false);
  const [criterionToDelete, setCriterionToDelete] = useState<Criterion | null>(null);
  const [confirmRestoreCriteriaModal, setConfirmRestoreCriteriaModal] = useState(false);
  const [newCriterionForm, setNewCriterionForm] = useState<Omit<Criterion, 'id'>>({
    name: '',
    question: '',
    lowLabel: '1 Below average',
    midLabel: '3 Average',
    highLabel: '5 Amazing',
  });

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (authenticateAdmin(pinInput)) {
      setPinError(false);
      setPinInput('');
    } else {
      setPinError(true);
    }
  };

  // If not authenticated, display PIN lock prompt
  if (!isAdminAuthenticated) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="bg-white border border-brand-tan p-8 rounded-3xl shadow-xl">
          <div className="w-14 h-14 rounded-2xl bg-brand-red/10 border border-brand-red/30 flex items-center justify-center mx-auto mb-4 text-brand-red">
            <Lock className="w-7 h-7" />
          </div>

          <h2 className="text-2xl font-black text-brand-black">Judge Admin Portal</h2>
          <p className="text-xs text-brand-subtext mt-1 mb-6">
            Enter the admin PIN to access standings, schedule management, ministries, and voting settings.
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <input
                type="password"
                maxLength={6}
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="Enter Admin PIN"
                className="w-full text-center tracking-widest text-lg font-mono py-3 px-4 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black focus:outline-none focus:border-brand-red/60 shadow-sm"
              />
              {pinError && (
                <p className="text-xs text-brand-red mt-2 font-medium">
                  Invalid PIN. Please try again.
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-brand-red hover:bg-red-600 text-white font-bold rounded-xl shadow-md shadow-brand-red/20 transition-all"
            >
              Unlock Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12 pb-24">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-brand-tan pb-6 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-tan/30 text-brand-black text-xs font-bold uppercase tracking-wider border border-brand-tan">
              <Unlock className="w-3.5 h-3.5 text-brand-red" />
              <span>NEXT Ventures Admin Console</span>
            </div>
          </div>
          <h1 className="text-3xl font-extrabold text-brand-black">
            Organizer Dashboard
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportCSV}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-brand-tan/20 hover:bg-brand-tan/40 text-brand-black text-xs font-bold border border-brand-tan transition-all"
          >
            <Download className="w-4 h-4 text-brand-red" />
            <span>Export Results CSV</span>
          </button>

          <button
            onClick={logoutAdmin}
            className="px-3 py-2 rounded-xl text-xs font-semibold text-brand-subtext hover:text-brand-black hover:bg-brand-tan/20 transition-colors"
          >
            Lock Admin
          </button>
        </div>
      </div>

      {/* Admin Sub-Tabs */}
      <div className="flex border-b border-brand-tan mb-8 overflow-x-auto">
        <button
          onClick={() => setActiveAdminTab('standings')}
          className={`py-3 px-4 text-xs sm:text-sm font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-all ${
            activeAdminTab === 'standings'
              ? 'border-brand-red text-brand-red'
              : 'border-transparent text-brand-subtext hover:text-brand-black'
          }`}
        >
          <Trophy className="w-4 h-4" />
          Live Standings
        </button>

        <button
          onClick={() => setActiveAdminTab('schedule')}
          className={`py-3 px-4 text-xs sm:text-sm font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-all ${
            activeAdminTab === 'schedule'
              ? 'border-brand-red text-brand-red'
              : 'border-transparent text-brand-subtext hover:text-brand-black'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Schedule ({schedule.length})
        </button>

        <button
          onClick={() => setActiveAdminTab('projects')}
          className={`py-3 px-4 text-xs sm:text-sm font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-all ${
            activeAdminTab === 'projects'
              ? 'border-brand-red text-brand-red'
              : 'border-transparent text-brand-subtext hover:text-brand-black'
          }`}
        >
          <Layers className="w-4 h-4" />
          Ministries ({projects.length})
        </button>

        <button
          onClick={() => setActiveAdminTab('criteria')}
          className={`py-3 px-4 text-xs sm:text-sm font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-all ${
            activeAdminTab === 'criteria'
              ? 'border-brand-red text-brand-red'
              : 'border-transparent text-brand-subtext hover:text-brand-black'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Criteria ({criteria.length})
        </button>

        <button
          onClick={() => setActiveAdminTab('ballots')}
          className={`py-3 px-4 text-xs sm:text-sm font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-all ${
            activeAdminTab === 'ballots'
              ? 'border-brand-red text-brand-red'
              : 'border-transparent text-brand-subtext hover:text-brand-black'
          }`}
        >
          <Users className="w-4 h-4" />
          Ballots ({ballots.length})
        </button>

        <button
          onClick={() => setActiveAdminTab('settings')}
          className={`py-3 px-4 text-xs sm:text-sm font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-all ${
            activeAdminTab === 'settings'
              ? 'border-brand-red text-brand-red'
              : 'border-transparent text-brand-subtext hover:text-brand-black'
          }`}
        >
          <SettingsIcon className="w-4 h-4" />
          Settings
        </button>
      </div>

      {/* TAB 0: Live Grant Standings & Tally */}
      {activeAdminTab === 'standings' && (
        <div className="space-y-6">
          {/* Refresh Standings Action */}
          <div className="flex justify-end items-center">
            <button
              type="button"
              onClick={() => refreshAllData()}
              disabled={isRefreshing}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-brand-tan/30 text-brand-black text-xs font-bold rounded-xl border border-brand-tan transition-all shadow-sm shrink-0 disabled:opacity-50 active:scale-95"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-brand-red ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>{isRefreshing ? 'Refreshing Standings...' : 'Refresh Standings Now'}</span>
            </button>
          </div>

          {/* Summary Stats Row */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-white border border-brand-tan p-5 rounded-2xl shadow-sm">
              <div className="text-xs text-brand-subtext font-bold uppercase tracking-wider mb-1">
                Total Ballots
              </div>
              <div className="text-2xl font-black text-brand-black">
                {ballots.filter(b => b.isSubmitted).length}
                <span className="text-xs font-normal text-brand-subtext ml-1.5">
                  ({ballots.length} total)
                </span>
              </div>
              <div className="text-[11px] text-brand-subtext mt-1">
                <span className="font-bold text-emerald-700">
                  {ballots.filter(b => b.isSubmitted).length} Submitted
                </span>
                {' • '}
                <span className="font-medium text-amber-700">
                  {ballots.filter(b => !b.isSubmitted).length} In Progress
                </span>
              </div>
            </div>

            <div className="bg-white border border-brand-tan p-5 rounded-2xl shadow-sm">
              <div className="text-xs text-brand-subtext font-bold uppercase tracking-wider mb-1">
                Staff Multiplier
              </div>
              <div className="text-2xl font-black text-purple-700">
                {settings.staffVoteWeight || 3.0}x
              </div>
              <div className="text-[11px] text-brand-subtext mt-1">
                Staff votes weighted {settings.staffVoteWeight || 3.0}x
              </div>
            </div>

            <div className="bg-white border border-brand-tan p-5 rounded-2xl shadow-sm">
              <div className="text-xs text-brand-subtext font-bold uppercase tracking-wider mb-1">
                Current #1 Projected
              </div>
              <div className="text-lg font-black text-brand-red truncate mt-1">
                {leaderboard[0] ? leaderboard[0].project.title : '—'}
              </div>
              <div className="text-[11px] text-brand-subtext">
                {leaderboard[0] ? `${leaderboard[0].totalScore} pts ($25,000 Grant)` : 'Awaiting Ballots'}
              </div>
            </div>

            <div className="bg-white border border-brand-tan p-5 rounded-2xl shadow-sm flex flex-col justify-between">
              <div>
                <div className="text-xs text-brand-subtext font-bold uppercase tracking-wider mb-1">
                  Export Full Data
                </div>
                <div className="text-[11px] text-brand-subtext">
                  Download spreadsheet with weighted tallies
                </div>
              </div>
              <button
                onClick={exportCSV}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-brand-tan/20 hover:bg-brand-tan/40 text-brand-black text-xs font-bold rounded-xl border border-brand-tan transition-all"
              >
                <Download className="w-3.5 h-3.5 text-brand-red" />
                <span>Export CSV</span>
              </button>
            </div>
          </div>

          {/* Top 5 Grant Winners Showcase */}
          <div className="bg-white border border-brand-tan p-6 rounded-3xl shadow-sm">
            <div className="flex items-center justify-between border-b border-brand-tan pb-4 mb-6">
              <div>
                <h3 className="text-base font-extrabold text-brand-black flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-brand-red" />
                  Top 5 Grant Finalists (Weighted Winners)
                </h3>
                <p className="text-xs text-brand-subtext mt-0.5">
                  Calculated using judge criteria ratings with staff votes weighted at {settings.staffVoteWeight || 3.0}x.
                </p>
              </div>
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                $75,000 Total Grants
              </span>
            </div>

            {ballots.length === 0 ? (
              <div className="p-8 text-center text-brand-subtext">
                <Trophy className="w-10 h-10 mx-auto mb-2 text-brand-warmgray" />
                <h4 className="font-bold text-brand-black text-sm">No Ballots Submitted Yet</h4>
                <p className="text-xs mt-1">Seed mock ballots in "Event Controls & Staff Weight" tab to test the tally calculation.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                {settings.prizeTiers.map((tier) => {
                  const summary = leaderboard[tier.rank - 1];
                  const is1st = tier.rank === 1;

                  return (
                    <div
                      key={tier.rank}
                      className={`p-4 rounded-2xl border text-center transition-all ${
                        is1st
                          ? 'bg-gradient-to-b from-red-50 to-white border-2 border-brand-red shadow-md'
                          : 'bg-[#F7F6F4] border-brand-tan text-brand-black'
                      }`}
                    >
                      <div className={`text-[10px] font-black uppercase tracking-wider mb-1 ${
                        is1st ? 'text-brand-red' : 'text-brand-subtext'
                      }`}>
                        {tier.place}
                      </div>
                      <div className={`text-lg font-black mb-2 ${is1st ? 'text-brand-red' : 'text-brand-black'}`}>
                        {tier.amount}
                      </div>

                      {summary ? (
                        <div className="pt-2 border-t border-brand-tan/60 text-left">
                          <div className="font-bold text-xs text-brand-black truncate" title={summary.project.title}>
                            {summary.project.title}
                          </div>
                          <div className="text-[11px] text-brand-subtext truncate mt-0.5">
                            {summary.project.presenter}
                          </div>
                          <div className="mt-2 pt-2 border-t border-brand-tan/40 flex items-center justify-between text-[11px]">
                            <span className="font-bold text-brand-black">{summary.totalScore} pts</span>
                            <span className="text-[10px] text-brand-subtext font-normal">
                              ({summary.staffBallotCount} staff, {summary.audienceBallotCount} aud)
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-brand-subtext italic mt-2">Awaiting Tally</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Full 12-Ministry Organizer Standings Table */}
          <div className="bg-white border border-brand-tan rounded-3xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-brand-tan flex items-center justify-between">
              <div>
                <h3 className="text-base font-extrabold text-brand-black">
                  Full Ministry Standings & Weighted Scoring Breakdown
                </h3>
                <p className="text-xs text-brand-subtext mt-0.5">
                  Private organizer audit table showing weighted points, unweighted raw scores, and staff vs audience participation.
                </p>
              </div>
              <span className="text-xs text-brand-subtext font-medium">
                {projects.length} Ministries Evaluated
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-brand-tan bg-[#F7F6F4] text-brand-subtext font-semibold">
                    <th className="py-3 px-4 w-12 text-center">Rank</th>
                    <th className="py-3 px-4">Ministry & Presenter</th>
                    <th className="py-3 px-4 text-right">Weighted Score</th>
                    <th className="py-3 px-4 text-right">Raw Unweighted</th>
                    <th className="py-3 px-4 text-center">Staff Ballots</th>
                    <th className="py-3 px-4 text-center">Audience Ballots</th>
                    <th className="py-3 px-4 text-right">Avg Rating</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-tan/60">
                  {leaderboard.map((item) => {
                    const isTop5 = item.rank <= 5;
                    return (
                      <tr 
                        key={item.project.id} 
                        className={`hover:bg-brand-offwhite/40 transition-colors ${
                          isTop5 ? 'bg-brand-red/[0.02]' : ''
                        }`}
                      >
                        <td className="py-3 px-4 text-center">
                          <span className={`w-6 h-6 rounded-md inline-flex items-center justify-center font-bold text-xs ${
                            item.rank === 1
                              ? 'bg-brand-red text-white'
                              : isTop5
                              ? 'bg-brand-black text-white'
                              : 'bg-brand-tan/40 text-brand-black'
                          }`}>
                            #{item.rank}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-brand-black flex items-center gap-2">
                            <span>{item.project.title}</span>
                            {isTop5 && (
                              <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
                                {settings.prizeTiers[item.rank - 1]?.amount}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-brand-subtext">{item.project.presenter}</div>
                        </td>
                        <td className="py-3 px-4 text-right font-black text-sm text-brand-black">
                          {item.totalScore} <span className="text-[10px] text-brand-subtext font-normal">pts</span>
                        </td>
                        <td className="py-3 px-4 text-right text-brand-subtext font-mono">
                          {item.unweightedTotalScore} pts
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-purple-800">
                          {item.staffBallotCount}
                        </td>
                        <td className="py-3 px-4 text-center text-brand-subtext">
                          {item.audienceBallotCount}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="font-bold text-brand-black">
                            {item.averageScore.toFixed(2)} <span className="text-[10px] text-brand-subtext font-normal">/ 5</span>
                          </div>
                          {item.unweightedAverageScore > 0 && item.unweightedAverageScore !== item.averageScore && (
                            <div className="text-[10px] text-brand-subtext font-normal" title="Raw unweighted average rating">
                              raw {item.unweightedAverageScore.toFixed(2)}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 1: Schedule Manager */}
      {activeAdminTab === 'schedule' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-brand-black">
                Event Schedule ({schedule.length} Sessions)
              </h3>
              <p className="text-xs text-brand-subtext">
                Manage timeline sessions, times, and descriptions
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setConfirmRestoreScheduleModal(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-brand-offwhite text-brand-black border border-brand-tan text-xs font-semibold rounded-xl transition-all shadow-sm"
                title="Restore default Shark Tank schedule"
              >
                <RotateCcw className="w-3.5 h-3.5 text-brand-subtext" />
                Restore Default Schedule
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewScheduleForm({
                    date: adminScheduleDayFilter !== 'all' ? adminScheduleDayFilter : 'Tuesday, September 29, 2026',
                    time: '',
                    title: '',
                    description: '',
                    speakerOrLeader: '',
                    category: 'general',
                    isCurrent: false,
                  });
                  setIsAddingScheduleItem(true);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-red hover:bg-red-600 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Schedule Session
              </button>
            </div>
          </div>

          {/* Add Schedule Item Form Drawer */}
          {isAddingScheduleItem && (
            <div className="bg-white border border-brand-red/40 p-6 rounded-3xl shadow-xl space-y-4 animate-fade-in">
              <div className="flex items-center justify-between border-b border-brand-tan pb-3">
                <h4 className="font-bold text-brand-black text-sm">Add New Schedule Session</h4>
                <button onClick={() => setIsAddingScheduleItem(false)} className="text-brand-subtext hover:text-brand-black">✕</button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="sm:col-span-2">
                  <label className="block text-brand-subtext font-semibold mb-1">Date / Event Day</label>
                  <input
                    type="text"
                    value={newScheduleForm.date || ''}
                    onChange={(e) => setNewScheduleForm({ ...newScheduleForm, date: e.target.value })}
                    placeholder="e.g. Tuesday, September 29, 2026"
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setNewScheduleForm({ ...newScheduleForm, date: 'Tuesday, September 29, 2026' })}
                      className="text-[11px] px-2.5 py-1 rounded-lg bg-brand-tan/30 hover:bg-brand-tan text-brand-black font-semibold transition-colors flex items-center gap-1"
                    >
                      <Calendar className="w-3 h-3 text-brand-red" />
                      Day 1: Tuesday, Sep 29
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewScheduleForm({ ...newScheduleForm, date: 'Wednesday, September 30, 2026' })}
                      className="text-[11px] px-2.5 py-1 rounded-lg bg-brand-tan/30 hover:bg-brand-tan text-brand-black font-semibold transition-colors flex items-center gap-1"
                    >
                      <Calendar className="w-3 h-3 text-brand-red" />
                      Day 2: Wednesday, Sep 30
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Time (e.g., 6:30 PM)</label>
                  <input
                    type="text"
                    value={newScheduleForm.time}
                    onChange={(e) => setNewScheduleForm({ ...newScheduleForm, time: e.target.value })}
                    placeholder="e.g. 6:30 PM"
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Category</label>
                  <select
                    value={newScheduleForm.category}
                    onChange={(e) => setNewScheduleForm({ ...newScheduleForm, category: e.target.value as any })}
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  >
                    <option value="general">General Session</option>
                    <option value="pitch">Pitch Presentations</option>
                    <option value="break">Intermission / Fellowship Break</option>
                    <option value="voting">Judges Voting Window</option>
                    <option value="awards">Grant Awards / Reveal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Session Title</label>
                  <input
                    type="text"
                    value={newScheduleForm.title}
                    onChange={(e) => setNewScheduleForm({ ...newScheduleForm, title: e.target.value })}
                    placeholder="e.g. Pitch Block 1: Ministries 1–6"
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Speaker / Leader</label>
                  <input
                    type="text"
                    value={newScheduleForm.speakerOrLeader}
                    onChange={(e) => setNewScheduleForm({ ...newScheduleForm, speakerOrLeader: e.target.value })}
                    placeholder="e.g. Event Emcee / Pitches 1–6"
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-brand-subtext font-semibold mb-1">Description</label>
                  <textarea
                    rows={2}
                    value={newScheduleForm.description}
                    onChange={(e) => setNewScheduleForm({ ...newScheduleForm, description: e.target.value })}
                    placeholder="Details about what happens during this time..."
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-brand-tan">
                <button
                  type="button"
                  onClick={() => setIsAddingScheduleItem(false)}
                  className="px-3 py-1.5 text-xs text-brand-subtext hover:text-brand-black"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (newScheduleForm.title.trim() && newScheduleForm.time.trim()) {
                      addScheduleItem(newScheduleForm);
                      setIsAddingScheduleItem(false);
                    }
                  }}
                  className="px-4 py-2 bg-brand-red hover:bg-red-600 text-white text-xs font-bold rounded-xl shadow-sm"
                >
                  Save Session
                </button>
              </div>
            </div>
          )}

          {/* Day Filter Tabs */}
          {adminUniqueDays.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
              <button
                type="button"
                onClick={() => setAdminScheduleDayFilter('all')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all shadow-sm ${
                  adminScheduleDayFilter === 'all'
                    ? 'bg-brand-black text-white'
                    : 'bg-white text-brand-subtext hover:text-brand-black border border-brand-tan hover:bg-brand-offwhite'
                }`}
              >
                All Sessions ({schedule.length})
              </button>
              {adminUniqueDays.map((day, idx) => {
                const count = schedule.filter((s) => (s.date?.trim() || 'General Schedule') === day).length;
                const isSelected = adminScheduleDayFilter === day;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setAdminScheduleDayFilter(day)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm ${
                      isSelected
                        ? 'bg-brand-red text-white'
                        : 'bg-white text-brand-subtext hover:text-brand-black border border-brand-tan hover:bg-brand-offwhite'
                    }`}
                  >
                    <Calendar className="w-3.5 h-3.5" />
                    <span>Day {idx + 1}: {day.replace(/^Day\s*\d+\s*[-–—:]\s*/i, '')}</span>
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                      isSelected ? 'bg-white/20 text-white' : 'bg-brand-tan/40 text-brand-black'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* List of Schedule Items */}
          <div className="divide-y divide-brand-tan/70 bg-white border border-brand-tan rounded-2xl overflow-hidden shadow-sm">
            {filteredAdminSchedule.map((item) => (
              <div key={item.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-brand-offwhite/20 transition-colors">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.date && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-black bg-brand-tan/40 px-2.5 py-0.5 rounded-md border border-brand-warmgray/30">
                        <Calendar className="w-3 h-3 text-brand-red" />
                        {item.date}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-xs font-mono font-extrabold text-brand-red bg-brand-red/10 px-2.5 py-0.5 rounded-md border border-brand-red/20">
                      <Clock className="w-3 h-3" />
                      {item.time}
                    </span>
                    <span className="font-bold text-brand-black text-sm">{item.title}</span>
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-brand-tan/40 text-brand-black">
                      {item.category}
                    </span>
                  </div>
                  {item.speakerOrLeader && (
                    <div className="text-xs text-brand-subtext">
                      Leader: <span className="text-brand-black font-medium">{item.speakerOrLeader}</span>
                    </div>
                  )}
                  <p className="text-xs text-brand-subtext line-clamp-1">{item.description}</p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setEditingScheduleItem(item)}
                    className="p-2 rounded-lg bg-white hover:bg-brand-tan/20 text-brand-subtext hover:text-brand-black border border-brand-tan shadow-sm"
                    aria-label="Edit session"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setScheduleItemToDelete(item)}
                    className="p-2 rounded-lg bg-white hover:bg-red-50 text-brand-subtext hover:text-brand-red border border-brand-tan shadow-sm transition-colors"
                    aria-label="Delete session"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Edit Schedule Item Modal */}
          {editingScheduleItem && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm overflow-y-auto">
              <div className="w-full max-w-lg bg-white border border-brand-tan rounded-3xl p-6 shadow-2xl text-xs space-y-3 text-brand-black my-auto">
                <h3 className="text-sm font-bold text-brand-black border-b border-brand-tan pb-2">
                  Edit Schedule Session: {editingScheduleItem.title}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-brand-subtext font-semibold mb-1">Date / Event Day</label>
                    <input
                      type="text"
                      value={editingScheduleItem.date || ''}
                      onChange={(e) => setEditingScheduleItem({ ...editingScheduleItem, date: e.target.value })}
                      placeholder="e.g. Tuesday, September 29, 2026"
                      className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                    />
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setEditingScheduleItem({ ...editingScheduleItem, date: 'Tuesday, September 29, 2026' })}
                        className="text-[11px] px-2.5 py-1 rounded-lg bg-brand-tan/30 hover:bg-brand-tan text-brand-black font-semibold transition-colors flex items-center gap-1"
                      >
                        <Calendar className="w-3 h-3 text-brand-red" />
                        Day 1: Tuesday, Sep 29
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingScheduleItem({ ...editingScheduleItem, date: 'Wednesday, September 30, 2026' })}
                        className="text-[11px] px-2.5 py-1 rounded-lg bg-brand-tan/30 hover:bg-brand-tan text-brand-black font-semibold transition-colors flex items-center gap-1"
                      >
                        <Calendar className="w-3 h-3 text-brand-red" />
                        Day 2: Wednesday, Sep 30
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-brand-subtext font-semibold mb-1">Time</label>
                    <input
                      type="text"
                      value={editingScheduleItem.time}
                      onChange={(e) => setEditingScheduleItem({ ...editingScheduleItem, time: e.target.value })}
                      className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                    />
                  </div>
                  <div>
                    <label className="block text-brand-subtext font-semibold mb-1">Category</label>
                    <select
                      value={editingScheduleItem.category}
                      onChange={(e) => setEditingScheduleItem({ ...editingScheduleItem, category: e.target.value as any })}
                      className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                    >
                      <option value="general">General Session</option>
                      <option value="pitch">Pitch Presentations</option>
                      <option value="break">Intermission / Fellowship Break</option>
                      <option value="voting">Judges Voting Window</option>
                      <option value="awards">Grant Awards / Reveal</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-brand-subtext font-semibold mb-1">Session Title</label>
                    <input
                      type="text"
                      value={editingScheduleItem.title}
                      onChange={(e) => setEditingScheduleItem({ ...editingScheduleItem, title: e.target.value })}
                      className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-brand-subtext font-semibold mb-1">Speaker / Leader</label>
                    <input
                      type="text"
                      value={editingScheduleItem.speakerOrLeader || ''}
                      onChange={(e) => setEditingScheduleItem({ ...editingScheduleItem, speakerOrLeader: e.target.value })}
                      className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-brand-subtext font-semibold mb-1">Description</label>
                    <textarea
                      rows={3}
                      value={editingScheduleItem.description}
                      onChange={(e) => setEditingScheduleItem({ ...editingScheduleItem, description: e.target.value })}
                      className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-brand-tan">
                  <button type="button" onClick={() => setEditingScheduleItem(null)} className="px-3 py-1.5 text-brand-subtext">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      updateScheduleItem(editingScheduleItem);
                      setEditingScheduleItem(null);
                    }}
                    className="px-4 py-1.5 bg-brand-red text-white font-bold rounded-lg shadow-sm"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: Settings & Multipliers (No phase switcher - open at all times) */}
      {activeAdminTab === 'settings' && (
        <div className="space-y-8">
          {/* Staff Voting Weight Settings */}
          <div className="bg-white border-2 border-purple-200 p-6 rounded-3xl shadow-sm">
            <div className="flex items-center gap-2 text-purple-800 font-bold text-xs uppercase tracking-wider mb-1">
              <Shield className="w-4 h-4 text-purple-600" />
              <span>Staff Weighted Voting Multiplier</span>
            </div>
            <h3 className="text-base font-extrabold text-brand-black mb-1">
              Staff Multiplier
            </h3>
            <p className="text-xs text-brand-subtext mb-6">
              When authenticated in this admin section, staff votes are weighted with this multiplier. Normal attendee ballots default to 1x.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <label className="block text-brand-subtext uppercase font-semibold mb-1">
                  Staff Vote Multiplier (Weight)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    max="20"
                    value={settings.staffVoteWeight || 3.0}
                    onChange={(e) => updateSettings({ staffVoteWeight: parseFloat(e.target.value) || 1.0 })}
                    className="w-28 p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black font-bold focus:border-purple-600 text-sm"
                  />
                  <div className="flex gap-1.5">
                    {[2, 3, 5, 10].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => updateSettings({ staffVoteWeight: num })}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                          (settings.staffVoteWeight || 3.0) === num
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'bg-white text-brand-black border-brand-tan hover:border-brand-warmgray'
                        }`}
                      >
                        {num}x
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-brand-subtext mt-1.5">
                  1 staff vote = <strong>{settings.staffVoteWeight || 3.0}</strong> audience votes.
                </p>
              </div>

              <div>
                <label className="block text-brand-subtext uppercase font-semibold mb-1">
                  Admin PIN Code
                </label>
                <input
                  type="text"
                  value={settings.adminPin || '1616'}
                  onChange={(e) => updateSettings({ adminPin: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black font-mono font-bold focus:border-brand-red/60 text-sm"
                />
                <p className="text-[11px] text-brand-subtext mt-1.5">
                  The security PIN used to unlock this organizer portal.
                </p>
              </div>
            </div>
          </div>

          {/* Grant Winning Amounts & Prize Tiers */}
          <div className="bg-white border-2 border-brand-tan p-6 rounded-3xl shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 text-brand-red font-bold text-xs uppercase tracking-wider mb-1">
                  <Trophy className="w-4 h-4 text-brand-red" />
                  <span>Grant Awards & Financial Tiers</span>
                </div>
                <h3 className="text-base font-extrabold text-brand-black">
                  Grant Winning Amounts
                </h3>
                <p className="text-xs text-brand-subtext">
                  Configure the official grant amounts awarded to finalists. These amounts update live on the judges’ ballots and leaderboard standings.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => updatePrizeTiers(DEFAULT_PRIZE_TIERS)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-brand-offwhite text-brand-black border border-brand-tan text-xs font-semibold rounded-xl transition-all shadow-sm shrink-0"
                  title="Reset to default grant tiers ($25k, $20k, $15k, $10k, $5k)"
                >
                  <RotateCcw className="w-3.5 h-3.5 text-brand-subtext" />
                  Restore Defaults
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const nextRank = settings.prizeTiers.length + 1;
                    const suffix = nextRank === 6 ? '6th' : nextRank === 7 ? '7th' : `${nextRank}th`;
                    const updated = [
                      ...settings.prizeTiers,
                      { place: `${suffix} Place`, amount: '$2,500', rank: nextRank },
                    ];
                    updatePrizeTiers(updated);
                  }}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-red hover:bg-red-600 text-white text-xs font-bold rounded-xl transition-all shadow-sm shrink-0"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Tier
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {settings.prizeTiers.map((tier, idx) => (
                <div
                  key={tier.rank}
                  className="p-4 rounded-2xl bg-[#F7F6F4] border border-brand-tan space-y-2.5 relative group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-extrabold uppercase tracking-wider text-brand-subtext">
                      Rank #{tier.rank}
                    </span>
                    {settings.prizeTiers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updated = settings.prizeTiers
                            .filter((_, i) => i !== idx)
                            .map((t, i) => ({ ...t, rank: i + 1 }));
                          updatePrizeTiers(updated);
                        }}
                        className="opacity-60 hover:opacity-100 hover:text-brand-red p-1 transition-all"
                        title="Remove prize tier"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase font-bold text-brand-subtext mb-1">
                      Place Label
                    </label>
                    <input
                      type="text"
                      value={tier.place}
                      onChange={(e) => {
                        const updated = settings.prizeTiers.map((t, i) =>
                          i === idx ? { ...t, place: e.target.value } : t
                        );
                        updatePrizeTiers(updated);
                      }}
                      className="w-full p-2 rounded-xl bg-white border border-brand-tan text-xs font-semibold text-brand-black focus:border-brand-red/60 shadow-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase font-bold text-brand-subtext mb-1">
                      Grant Amount
                    </label>
                    <input
                      type="text"
                      value={tier.amount}
                      onChange={(e) => {
                        const updated = settings.prizeTiers.map((t, i) =>
                          i === idx ? { ...t, amount: e.target.value } : t
                        );
                        updatePrizeTiers(updated);
                      }}
                      placeholder="$25,000"
                      className="w-full p-2 rounded-xl bg-white border border-brand-tan text-sm font-extrabold text-brand-black focus:border-brand-red/60 shadow-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Event Configuration Settings */}
          <div className="bg-white border border-brand-tan p-6 rounded-3xl shadow-sm">
            <h3 className="text-base font-extrabold text-brand-black mb-4">
              General Event Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <label className="block text-brand-subtext uppercase font-semibold mb-1">
                  Event Title
                </label>
                <input
                  type="text"
                  value={settings.eventName}
                  onChange={(e) => updateSettings({ eventName: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black focus:border-brand-red/60"
                />
              </div>

              <div>
                <label className="block text-brand-subtext uppercase font-semibold mb-1">
                  Venue / Location
                </label>
                <input
                  type="text"
                  value={settings.venue}
                  onChange={(e) => updateSettings({ venue: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black focus:border-brand-red/60"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-brand-subtext uppercase font-semibold mb-1">
                  Event Date
                </label>
                <input
                  type="text"
                  value={settings.eventDate}
                  onChange={(e) => updateSettings({ eventDate: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black focus:border-brand-red/60"
                />
              </div>

              {/* Digital Program Book URL */}
              <div className="sm:col-span-2 pt-3 border-t border-brand-tan/60">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-brand-subtext uppercase font-semibold">
                    Digital Program Book Link (URL)
                  </label>
                  {settings.programBookUrl && (
                    <a
                      href={settings.programBookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-brand-red hover:underline font-bold"
                    >
                      <span>Test / Preview Link</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="relative">
                  <BookOpen className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-subtext" />
                  <input
                    type="url"
                    value={settings.programBookUrl || ''}
                    onChange={(e) => updateSettings({ programBookUrl: e.target.value })}
                    placeholder="https://online.fliphtml5.com/... or https://drive.google.com/... or https://program.pdf"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black placeholder:text-brand-warmgray text-xs focus:outline-none focus:border-brand-red/60"
                  />
                </div>
                <p className="text-[11px] text-brand-subtext mt-1.5">
                  The URL or PDF link to the official digital event program. Attendees will see a <strong>"Program Book"</strong> option in the top navigation bar to open this link in a new tab.
                </p>
              </div>
            </div>
          </div>

          {/* Rehearsal & Testing Tools */}
          <div className="bg-white border border-brand-tan p-6 rounded-3xl shadow-sm">
            <h3 className="text-base font-extrabold text-brand-black mb-1">
              Rehearsal & Testing Tools
            </h3>
            <p className="text-xs text-brand-subtext mb-6">
              Seed realistic mock judge ballots across all 12 ministries to test tallies, or clear all test ballots before the official event begins.
            </p>

            <div className="flex flex-wrap gap-4 items-center">
              <button
                type="button"
                onClick={() => seedTestBallots(12)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-tan/20 hover:bg-brand-tan/40 text-brand-black text-xs font-bold border border-brand-tan transition-all"
              >
                <Play className="w-3.5 h-3.5 text-emerald-600" />
                <span>Seed 12 Mock Judge Ballots</span>
              </button>

              <button
                type="button"
                onClick={() => setConfirmResetModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-red/10 hover:bg-brand-red/20 text-brand-red text-xs font-bold border border-brand-red/20 transition-all"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reset / Clear All Votes</span>
              </button>

              <span className="text-xs text-brand-subtext">
                Currently stored: <strong className="text-brand-black">{ballots.length}</strong> judge ballots
              </span>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: Manage Projects */}
      {activeAdminTab === 'projects' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-brand-black">
                Pitching Ministries ({projects.length})
              </h3>
              <p className="text-xs text-brand-subtext">
                Manage the official finalists pitching for the 2026 grants
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setConfirmRestoreDefaultsModal(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-brand-offwhite text-brand-black border border-brand-tan text-xs font-semibold rounded-xl transition-all shadow-sm"
                title="Restore original 12 default ministries"
              >
                <RotateCcw className="w-3.5 h-3.5 text-brand-subtext" />
                Restore 12 Defaults
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewProjectForm({ title: '', presenter: '', website: '', purpose: '' });
                  setIsAddingProject(true);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-red hover:bg-red-600 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Ministry Pitch
              </button>
            </div>
          </div>

          {/* Missing Ministries Alert Banner */}
          {missingDefaultProjects.length > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-amber-50 border border-amber-300/80 rounded-2xl text-xs text-amber-950 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center text-amber-800 shrink-0">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-bold text-amber-900">
                    {missingDefaultProjects.length === 1 
                      ? '1 Official Ministry is Missing' 
                      : `${missingDefaultProjects.length} Official Ministries Missing`}
                  </p>
                  <p className="text-amber-800/90 mt-0.5">
                    {missingDefaultProjects.map((p) => `"${p.title}"`).join(', ')} was removed. You can restore it right now.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  missingDefaultProjects.forEach((p) => restoreProject(p.id));
                }}
                className="w-full sm:w-auto px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl shadow-sm shrink-0 transition-colors"
              >
                Restore {missingDefaultProjects.length === 1 ? 'Missing Ministry' : 'All Missing'}
              </button>
            </div>
          )}

          {/* Add Ministry Form Modal/Drawer */}
          {isAddingProject && (
            <div className="bg-white border border-brand-red/40 p-6 rounded-3xl shadow-xl space-y-4">
              <div className="flex items-center justify-between border-b border-brand-tan pb-3">
                <h4 className="font-bold text-brand-black text-sm">Add New Ministry Pitch Submission</h4>
                <button onClick={() => setIsAddingProject(false)} className="text-brand-subtext hover:text-brand-black">✕</button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Ministry Name *</label>
                  <input
                    type="text"
                    required
                    value={newProjectForm.title}
                    onChange={(e) => setNewProjectForm({ ...newProjectForm, title: e.target.value })}
                    placeholder="e.g. Break Free Ministries"
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Ministry Leader *</label>
                  <input
                    type="text"
                    required
                    value={newProjectForm.presenter}
                    onChange={(e) => setNewProjectForm({ ...newProjectForm, presenter: e.target.value })}
                    placeholder="e.g. Michael Swalley"
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-brand-subtext font-semibold mb-1">Website URL (optional)</label>
                  <input
                    type="text"
                    value={newProjectForm.website}
                    onChange={(e) => setNewProjectForm({ ...newProjectForm, website: e.target.value })}
                    placeholder="e.g. https://breakfreeministries.org"
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-brand-subtext font-semibold mb-1">Ministry Purpose *</label>
                  <textarea
                    rows={3}
                    required
                    value={newProjectForm.purpose}
                    onChange={(e) => setNewProjectForm({ ...newProjectForm, purpose: e.target.value })}
                    placeholder="The core purpose and mission of this ministry..."
                    className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-brand-tan">
                <button
                  onClick={() => setIsAddingProject(false)}
                  className="px-3 py-1.5 text-xs text-brand-subtext hover:text-brand-black"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newProjectForm.title.trim()) {
                      addProject({
                        title: newProjectForm.title.trim(),
                        presenter: newProjectForm.presenter.trim(),
                        website: newProjectForm.website.trim(),
                        purpose: newProjectForm.purpose.trim(),
                      });
                      setIsAddingProject(false);
                    }
                  }}
                  className="px-4 py-2 bg-brand-red hover:bg-red-600 text-white text-xs font-bold rounded-xl shadow-sm"
                >
                  Save Ministry
                </button>
              </div>
            </div>
          )}

          {/* List of Current Ministries */}
          <div className="divide-y divide-brand-tan/70 bg-white border border-brand-tan rounded-2xl overflow-hidden shadow-sm">
            {projects.map((proj) => (
              <div key={proj.id} className="p-4 flex items-center justify-between gap-4 hover:bg-brand-offwhite/20 transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-extrabold text-brand-red">#{proj.order}</span>
                    <span className="font-bold text-brand-black text-sm truncate">{proj.title}</span>
                  </div>
                  <div className="text-xs text-brand-subtext truncate mt-0.5">
                    Presenter: <span className="text-brand-black font-medium">{proj.presenter}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setEditingProject(proj)}
                    className="p-2 rounded-lg bg-white hover:bg-brand-tan/20 text-brand-subtext hover:text-brand-black border border-brand-tan shadow-sm"
                    aria-label="Edit ministry"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setProjectToDelete(proj)}
                    className="p-2 rounded-lg bg-white hover:bg-red-50 text-brand-subtext hover:text-brand-red border border-brand-tan shadow-sm transition-colors"
                    aria-label={`Delete ${proj.title}`}
                    title={`Delete ${proj.title}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Edit Ministry Modal */}
          {editingProject && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm overflow-y-auto">
              <div className="w-full max-w-lg bg-white border border-brand-tan rounded-3xl p-6 shadow-2xl text-xs space-y-3 text-brand-black my-auto max-h-[90vh] overflow-y-auto">
                <h3 className="text-sm font-bold text-brand-black border-b border-brand-tan pb-2">
                  Edit Ministry #{editingProject.order}: {editingProject.title}
                </h3>
                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Ministry Name</label>
                  <input
                    type="text"
                    value={editingProject.title}
                    onChange={(e) => setEditingProject({ ...editingProject, title: e.target.value })}
                    className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Presenter / Ministry Leader</label>
                  <input
                    type="text"
                    value={editingProject.presenter}
                    onChange={(e) => setEditingProject({ ...editingProject, presenter: e.target.value })}
                    className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Website URL (optional)</label>
                  <input
                    type="text"
                    value={editingProject.website || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, website: e.target.value })}
                    className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>
                <div>
                  <label className="block text-brand-subtext font-semibold mb-1">Ministry Purpose</label>
                  <textarea
                    rows={4}
                    value={editingProject.purpose}
                    onChange={(e) => setEditingProject({ ...editingProject, purpose: e.target.value })}
                    className="w-full p-2 rounded-lg bg-[#F7F6F4] border border-brand-tan text-brand-black"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-brand-tan">
                  <button onClick={() => setEditingProject(null)} className="px-3 py-1.5 text-brand-subtext">
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      updateProject({
                        ...editingProject,
                        title: editingProject.title.trim(),
                        presenter: editingProject.presenter.trim(),
                        website: (editingProject.website || '').trim(),
                        purpose: editingProject.purpose.trim(),
                      });
                      setEditingProject(null);
                    }}
                    className="px-4 py-1.5 bg-brand-red text-white font-bold rounded-lg shadow-sm"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: Criteria Manager */}
      {activeAdminTab === 'criteria' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-brand-black">
                Official Judging Criteria ({criteria.length})
              </h3>
              <p className="text-xs text-brand-subtext">
                Judges evaluate each ministry against these {criteria.length} standards on a 1–5 scale.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setConfirmRestoreCriteriaModal(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-brand-offwhite text-brand-black border border-brand-tan text-xs font-semibold rounded-xl transition-all shadow-sm"
                title="Restore default 7 criteria"
              >
                <RotateCcw className="w-3.5 h-3.5 text-brand-subtext" />
                Restore Defaults
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewCriterionForm({
                    name: '',
                    question: '',
                    lowLabel: '1 Below average',
                    midLabel: '3 Average',
                    highLabel: '5 Amazing',
                  });
                  setIsAddingCriterion(true);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-red hover:bg-red-600 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Criterion
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {criteria.map((crit, idx) => (
              <div key={crit.id} className="bg-white border border-brand-tan p-5 rounded-2xl shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-5 h-5 rounded-full bg-brand-red/15 text-brand-red text-xs font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <h4 className="font-bold text-brand-black text-sm">{crit.name}</h4>
                    </div>
                    <p className="text-xs text-brand-subtext font-medium leading-relaxed">{crit.question}</p>
                    <div className="flex items-center gap-3 mt-3 text-[11px] text-brand-subtext flex-wrap">
                      <span>Low: <strong className="text-brand-black">{crit.lowLabel}</strong></span>
                      <span>•</span>
                      <span>Mid: <strong className="text-brand-black">{crit.midLabel || '3 Average'}</strong></span>
                      <span>•</span>
                      <span>High: <strong className="text-brand-black">{crit.highLabel}</strong></span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditingCriterion(crit)}
                      className="p-2 rounded-lg bg-white hover:bg-brand-tan/20 text-brand-subtext hover:text-brand-black border border-brand-tan shadow-sm transition-colors"
                      title="Edit criterion"
                      aria-label="Edit criterion"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCriterionToDelete(crit)}
                      className="p-2 rounded-lg bg-white hover:bg-red-50 text-brand-subtext hover:text-brand-red border border-brand-tan shadow-sm transition-colors"
                      title="Delete criterion"
                      aria-label="Delete criterion"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Delete Criterion Confirmation Modal */}
          {criterionToDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm">
              <div className="w-full max-w-md bg-white border border-brand-tan rounded-3xl p-6 shadow-2xl space-y-4">
                <div className="flex items-center gap-3 text-brand-red">
                  <div className="w-10 h-10 rounded-2xl bg-brand-red/10 border border-brand-red/20 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-5 h-5 text-brand-red" />
                  </div>
                  <div>
                    <h4 className="font-extrabold text-brand-black text-base">Delete Judging Criterion?</h4>
                    <p className="text-xs text-brand-subtext">This action removes it from the ballot</p>
                  </div>
                </div>
                <p className="text-xs text-brand-black/80 leading-relaxed bg-[#F7F6F4] p-3.5 rounded-2xl border border-brand-tan">
                  Are you sure you want to delete <strong className="text-brand-black">"{criterionToDelete.name}"</strong>? Judges will no longer evaluate ministries on this standard.
                </p>
                <div className="flex justify-end gap-2 pt-2 border-t border-brand-tan">
                  <button
                    type="button"
                    onClick={() => setCriterionToDelete(null)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-brand-subtext hover:text-brand-black"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      deleteCriterion(criterionToDelete.id);
                      setCriterionToDelete(null);
                    }}
                    className="px-4 py-2 rounded-xl bg-brand-red hover:bg-red-600 text-white text-xs font-bold shadow-md shadow-brand-red/20 transition-all"
                  >
                    Yes, Delete Criterion
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Restore Defaults Confirmation Modal */}
          {confirmRestoreCriteriaModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm">
              <div className="w-full max-w-md bg-white border border-brand-tan rounded-3xl p-6 shadow-2xl space-y-4">
                <h4 className="font-extrabold text-brand-black text-base">Restore 7 Default Criteria?</h4>
                <p className="text-xs text-brand-subtext leading-relaxed bg-[#F7F6F4] p-3.5 rounded-2xl border border-brand-tan">
                  This will reset all evaluation standards back to the 7 official NEXT Ventures judging criteria (Innovative, Impacting, Sustainable, Scalable, Diverse, Transferable, New).
                </p>
                <div className="flex justify-end gap-2 pt-2 border-t border-brand-tan">
                  <button
                    type="button"
                    onClick={() => setConfirmRestoreCriteriaModal(false)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-brand-subtext hover:text-brand-black"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      restoreDefaultCriteria();
                      setConfirmRestoreCriteriaModal(false);
                    }}
                    className="px-4 py-2 rounded-xl bg-brand-red hover:bg-red-600 text-white text-xs font-bold shadow-md"
                  >
                    Restore Defaults
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Criterion Modal */}
          {isAddingCriterion && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm">
              <div className="w-full max-w-lg bg-white border border-brand-tan rounded-3xl p-6 shadow-2xl space-y-4 text-xs">
                <div className="flex items-center justify-between border-b border-brand-tan pb-3">
                  <h4 className="font-bold text-brand-black text-sm">Add New Judging Criterion</h4>
                  <button onClick={() => setIsAddingCriterion(false)} className="text-brand-subtext hover:text-brand-black">✕</button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-brand-subtext font-semibold mb-1">Criterion Name</label>
                    <input
                      type="text"
                      value={newCriterionForm.name}
                      onChange={(e) => setNewCriterionForm({ ...newCriterionForm, name: e.target.value })}
                      placeholder="e.g. Kingdom Alignment"
                      className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black focus:border-brand-red/60"
                    />
                  </div>
                  <div>
                    <label className="block text-brand-subtext font-semibold mb-1">Guiding Question</label>
                    <textarea
                      rows={3}
                      value={newCriterionForm.question}
                      onChange={(e) => setNewCriterionForm({ ...newCriterionForm, question: e.target.value })}
                      placeholder="e.g. Does this ministry demonstrate clear gospel intentionality and alignment with kingdom values?"
                      className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black focus:border-brand-red/60"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-brand-subtext font-semibold mb-1">Low Label (Score 1)</label>
                      <input
                        type="text"
                        value={newCriterionForm.lowLabel}
                        onChange={(e) => setNewCriterionForm({ ...newCriterionForm, lowLabel: e.target.value })}
                        className="w-full p-2 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-subtext font-semibold mb-1">Mid Label (Score 3)</label>
                      <input
                        type="text"
                        value={newCriterionForm.midLabel}
                        onChange={(e) => setNewCriterionForm({ ...newCriterionForm, midLabel: e.target.value })}
                        className="w-full p-2 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-subtext font-semibold mb-1">High Label (Score 5)</label>
                      <input
                        type="text"
                        value={newCriterionForm.highLabel}
                        onChange={(e) => setNewCriterionForm({ ...newCriterionForm, highLabel: e.target.value })}
                        className="w-full p-2 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black text-xs"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t border-brand-tan">
                  <button
                    type="button"
                    onClick={() => setIsAddingCriterion(false)}
                    className="px-4 py-2 text-xs font-semibold text-brand-subtext hover:text-brand-black"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (newCriterionForm.name.trim() && newCriterionForm.question.trim()) {
                        addCriterion(newCriterionForm);
                        setIsAddingCriterion(false);
                      }
                    }}
                    className="px-4 py-2 bg-brand-red hover:bg-red-600 text-white text-xs font-bold rounded-xl shadow-sm"
                  >
                    Save Criterion
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Criterion Modal */}
          {editingCriterion && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm">
              <div className="w-full max-w-lg bg-white border border-brand-tan rounded-3xl p-6 shadow-2xl text-xs space-y-4 text-brand-black">
                <div className="flex items-center justify-between border-b border-brand-tan pb-3">
                  <h3 className="text-sm font-bold text-brand-black">
                    Edit Criterion: {editingCriterion.name}
                  </h3>
                  <button onClick={() => setEditingCriterion(null)} className="text-brand-subtext hover:text-brand-black">✕</button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-brand-subtext font-semibold mb-1">Criterion Name</label>
                    <input
                      type="text"
                      value={editingCriterion.name}
                      onChange={(e) => setEditingCriterion({ ...editingCriterion, name: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black focus:border-brand-red/60"
                    />
                  </div>
                  <div>
                    <label className="block text-brand-subtext font-semibold mb-1">Guiding Question</label>
                    <textarea
                      rows={3}
                      value={editingCriterion.question}
                      onChange={(e) => setEditingCriterion({ ...editingCriterion, question: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black focus:border-brand-red/60"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-brand-subtext font-semibold mb-1">Low Label (Score 1)</label>
                      <input
                        type="text"
                        value={editingCriterion.lowLabel || '1 Below average'}
                        onChange={(e) => setEditingCriterion({ ...editingCriterion, lowLabel: e.target.value })}
                        className="w-full p-2 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-subtext font-semibold mb-1">Mid Label (Score 3)</label>
                      <input
                        type="text"
                        value={editingCriterion.midLabel || '3 Average'}
                        onChange={(e) => setEditingCriterion({ ...editingCriterion, midLabel: e.target.value })}
                        className="w-full p-2 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-brand-subtext font-semibold mb-1">High Label (Score 5)</label>
                      <input
                        type="text"
                        value={editingCriterion.highLabel || '5 Amazing'}
                        onChange={(e) => setEditingCriterion({ ...editingCriterion, highLabel: e.target.value })}
                        className="w-full p-2 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-brand-tan">
                  <button onClick={() => setEditingCriterion(null)} className="px-4 py-2 text-xs font-semibold text-brand-subtext hover:text-brand-black">
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      updateCriterion(editingCriterion);
                      setEditingCriterion(null);
                    }}
                    className="px-4 py-2 bg-brand-red hover:bg-red-600 text-white font-bold rounded-xl shadow-sm"
                  >
                    Save Criterion
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: Voter Ballots Audit */}
      {activeAdminTab === 'ballots' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-brand-black">
              Judge Ballots Log ({ballots.length})
            </h3>
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-tan/20 hover:bg-brand-tan/40 text-brand-black text-xs font-bold rounded-xl border border-brand-tan transition-all shadow-sm"
            >
              <Download className="w-3.5 h-3.5 text-brand-red" />
              Download CSV
            </button>
          </div>

          {ballots.length === 0 ? (
            <div className="bg-white border border-brand-tan p-8 rounded-2xl text-center text-brand-subtext text-sm shadow-sm">
              No ballots submitted yet.
            </div>
          ) : (
            <div className="border border-brand-tan rounded-2xl overflow-hidden bg-white shadow-sm">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-brand-tan text-brand-subtext font-semibold bg-[#F7F6F4]">
                    <th className="py-3 px-4">Judge / Voter</th>
                    <th className="py-3 px-4">Classification</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Device ID</th>
                    <th className="py-3 px-4">Ministries Rated</th>
                    <th className="py-3 px-4 text-right">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-tan/60">
                  {ballots.map((b) => (
                    <tr key={b.id} className="hover:bg-brand-offwhite/40">
                      <td className="py-3 px-4 font-bold text-brand-black">
                        <div>{b.voterName}</div>
                        {b.voterEmail && (
                          <div className="text-[11px] font-normal text-brand-subtext">{b.voterEmail}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {b.voterRole === 'staff' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-300">
                            <Shield className="w-3 h-3 text-purple-600" />
                            Staff ({b.weight || settings.staffVoteWeight || 3}x)
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-brand-tan/30 text-brand-black">
                            <Users className="w-3 h-3 text-brand-subtext" />
                            Audience (1x)
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {b.isSubmitted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            Submitted
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                            <Clock className="w-3 h-3 text-amber-600" />
                            In Progress ({Object.keys(b.scores).length}/{projects.length})
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono text-brand-subtext text-[11px]">{b.voterId}</td>
                      <td className="py-3 px-4 text-emerald-700 font-semibold">
                        {Object.keys(b.scores).length} of {projects.length}
                      </td>
                      <td className="py-3 px-4 text-right text-brand-subtext">
                        {new Date(b.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {confirmResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white border border-brand-red/40 rounded-2xl p-6 shadow-2xl text-center">
            <div className="w-12 h-12 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-base font-bold text-brand-black">Clear All Ballots?</h3>
            <p className="text-xs text-brand-subtext mt-1 mb-6">
              This will permanently delete all {ballots.length} cast votes. Only do this before the official event starts.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmResetModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-[#F7F6F4] text-brand-black text-xs font-semibold hover:bg-brand-tan/30 border border-brand-tan"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  resetAllBallots();
                  setConfirmResetModal(false);
                }}
                className="flex-1 py-2.5 rounded-xl bg-brand-red hover:bg-red-600 text-white text-xs font-bold shadow-md shadow-brand-red/30"
              >
                Yes, Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Project Confirmation Modal */}
      {projectToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-white border border-brand-red/30 rounded-3xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-red-100 text-brand-red flex items-center justify-center shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-brand-black">Delete Ministry?</h3>
                <p className="text-xs text-brand-subtext">This action requires confirmation</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-brand-offwhite border border-brand-tan text-xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="font-bold text-brand-black text-sm">{projectToDelete.title}</span>
                <span className="text-[11px] font-semibold text-brand-red">#{projectToDelete.order}</span>
              </div>
              <p className="text-brand-subtext">
                Presenter: <span className="font-medium text-brand-black">{projectToDelete.presenter}</span>
              </p>
            </div>

            <p className="text-xs text-brand-subtext leading-relaxed">
              Are you sure you want to delete <strong className="text-brand-black">{projectToDelete.title}</strong>? This will remove it from the pitch showcase, active voting ballots, and live scoring calculations.
            </p>

            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setProjectToDelete(null)}
                className="flex-1 py-2.5 rounded-xl bg-[#F7F6F4] text-brand-black text-xs font-semibold hover:bg-brand-tan/40 border border-brand-tan transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteProject(projectToDelete.id);
                  setProjectToDelete(null);
                }}
                className="flex-1 py-2.5 rounded-xl bg-brand-red hover:bg-red-600 text-white text-xs font-bold shadow-md shadow-brand-red/20 transition-all"
              >
                Yes, Delete Ministry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Defaults Confirmation Modal */}
      {confirmRestoreDefaultsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-white border border-brand-tan rounded-3xl p-6 shadow-2xl space-y-4 text-center">
            <div className="w-12 h-12 rounded-2xl bg-brand-tan/40 text-brand-black flex items-center justify-center mx-auto">
              <RotateCcw className="w-6 h-6 text-brand-black" />
            </div>
            <div>
              <h3 className="text-base font-bold text-brand-black">Restore 12 Official Ministries?</h3>
              <p className="text-xs text-brand-subtext mt-1">
                This will reset the ministry lineup back to the 12 pre-loaded NEXT Ventures pitch finalists.
              </p>
            </div>
            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setConfirmRestoreDefaultsModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-[#F7F6F4] text-brand-black text-xs font-semibold hover:bg-brand-tan/40 border border-brand-tan"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  restoreDefaultProjects();
                  setConfirmRestoreDefaultsModal(false);
                }}
                className="flex-1 py-2.5 rounded-xl bg-brand-black hover:bg-brand-darkgray text-white text-xs font-bold shadow-sm"
              >
                Restore 12 Ministries
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Schedule Session Confirmation Modal */}
      {scheduleItemToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-white border border-brand-red/30 rounded-3xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-red-100 text-brand-red flex items-center justify-center shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-brand-black">Delete Schedule Session?</h3>
                <p className="text-xs text-brand-subtext">This action requires confirmation</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-brand-offwhite border border-brand-tan text-xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="font-bold text-brand-black text-sm">{scheduleItemToDelete.title}</span>
                <span className="text-[11px] font-mono font-semibold text-brand-red">{scheduleItemToDelete.time}</span>
              </div>
              {scheduleItemToDelete.speakerOrLeader && (
                <p className="text-brand-subtext">
                  Leader: <span className="font-medium text-brand-black">{scheduleItemToDelete.speakerOrLeader}</span>
                </p>
              )}
            </div>

            <p className="text-xs text-brand-subtext leading-relaxed">
              Are you sure you want to delete <strong className="text-brand-black">{scheduleItemToDelete.title}</strong>? It will be removed from the public event timeline.
            </p>

            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setScheduleItemToDelete(null)}
                className="flex-1 py-2.5 rounded-xl bg-[#F7F6F4] text-brand-black text-xs font-semibold hover:bg-brand-tan/40 border border-brand-tan transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteScheduleItem(scheduleItemToDelete.id);
                  setScheduleItemToDelete(null);
                }}
                className="flex-1 py-2.5 rounded-xl bg-brand-red hover:bg-red-600 text-white text-xs font-bold shadow-md shadow-brand-red/20 transition-all"
              >
                Yes, Delete Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Default Schedule Confirmation Modal */}
      {confirmRestoreScheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-white border border-brand-tan rounded-3xl p-6 shadow-2xl space-y-4 text-center">
            <div className="w-12 h-12 rounded-2xl bg-brand-tan/40 text-brand-black flex items-center justify-center mx-auto">
              <RotateCcw className="w-6 h-6 text-brand-black" />
            </div>
            <div>
              <h3 className="text-base font-bold text-brand-black">Restore Default Schedule?</h3>
              <p className="text-xs text-brand-subtext mt-1">
                This will reset the timeline back to the standard Shark Tank 2026 event schedule.
              </p>
            </div>
            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setConfirmRestoreScheduleModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-[#F7F6F4] text-brand-black text-xs font-semibold hover:bg-brand-tan/40 border border-brand-tan"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  restoreDefaultSchedule();
                  setConfirmRestoreScheduleModal(false);
                }}
                className="flex-1 py-2.5 rounded-xl bg-brand-black hover:bg-brand-darkgray text-white text-xs font-bold shadow-sm"
              >
                Restore Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
